import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle,
  XCircle,
  Quote,
  Star // استيراد أيقونة النجمة
} from 'lucide-react';

// مكون جديد لتقييم النجوم
const StarRating = ({ rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-amber-400' : 'text-gray-300'}`}
        fill="currentColor"
      />
    );
  }
  return <div className="flex justify-end space-x-1 rtl:space-x-reverse">{stars}</div>;
};

const Contact = () => {
  // حالة النموذج لتخزين بيانات المستخدم
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  // حالة لتحديد حالة إرسال النموذج: null, 'success', or 'error'
  const [submissionStatus, setSubmissionStatus] = useState(null);

  // معالج التغييرات في حقول الإدخال
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // معالج إرسال النموذج
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);

    try {
      // محاكاة استدعاء API لإرسال البيانات
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmissionStatus('success');
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmissionStatus('error');
    }
  };

  // بيانات الاتصال
  const contactInfo = [
    {
      id: "phone",
      icon: <Phone className="w-5 h-5 text-blue-600" />,
      title: "اتصل بنا",
      details: ["012-345-6789", "011-234-5678"],
      color: "bg-blue-50"
    },
    {
      id: "email",
      icon: <Mail className="w-5 h-5 text-teal-600" />,
      title: "راسلنا",
      details: ["Elite@medicallab.com", "support@medicallab.com"],
      color: "bg-teal-50"
    },
    {
      id: "visit", 
      icon: <MapPin className="w-5 h-5 text-purple-600" />,
      title: "زورنا",
      details: ["القاهرة - حي النزهة", "الزقازيق - القومية"],
      color: "bg-purple-50"
    },
    {
      id: "hours",
      icon: <Clock className="w-5 h-5 text-amber-600" />,
      title: "مواعيد العمل",
      details: ["السبت - الخميس: 7:00 ص - 11:00 م", "الجمعة: 2:00 م - 11:00 م"],
      color: "bg-amber-50"
    }
  ];

  // بيانات شهادات العملاء الجديدة مع تقييم بالنجوم
  const testimonials = [
    {
      id: 1,
      text: "خدمة سريعة وممتازة، والنتائج دقيقة جدًا. فريق العمل محترف وودود للغاية.",
      author: "احمد محمد",
      rating: 5
    },
    {
      id: 2,
      text: "تجربة رائعة! مواعيد دقيقة والمكان نظيف جدًا. أنصح بالتعامل معهم بشدة.",
      author:  "سارة فارس",
      rating: 4
    },
    {
      id: 3,
      text: "احترافية عالية ودقة في المواعيد والنتائج. أفضل مكان للتحاليل الطبية.",
      author: "يوسف علي",
      rating: 5
    },
  ];

  return (
    <section id="contact" className="py-24 bg-slate-50 font-sans">
      <div className="container mx-auto px-4">
        {/* رأس القسم (Section Header) */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
            نحن في خدمتك
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            تواصل معنا
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            نعدك بالاهتمام بكل ما ترغب في معرفته، يرجى تقديم معلوماتك وستتلقى ردًا من ممثلي خدمة العملاء لدينا
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* نموذج الاتصال (Contact Form) */}
          <div className="bg-white shadow-lg rounded-2xl p-8 flex flex-col space-y-8 border border-gray-100">
            <div>
              <h3 className="text-2xl font-bold text-blue-900">أرسل لنا رسالة</h3>
              <p className="text-gray-600 mt-2">
                املأ النموذج أدناه وسنتواصل معك في أقرب وقت ممكن
              </p>
            </div>
            
            {/* رسالة حالة الإرسال */}
            {submissionStatus === 'success' && (
              <div className="bg-teal-50 border-r-4 border-teal-500 text-teal-800 p-4 rounded-md shadow-sm" role="alert">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 ml-2" />
                  <p className="font-semibold">تم إرسال رسالتك بنجاح!</p>
                </div>
                <p className="text-sm mt-1">سنتواصل معك قريباً.</p>
              </div>
            )}
            {submissionStatus === 'error' && (
              <div className="bg-rose-50 border-r-4 border-rose-500 text-rose-700 p-4 rounded-md shadow-sm" role="alert">
                <div className="flex items-center">
                  <XCircle className="w-5 h-5 ml-2" />
                  <p className="font-semibold">حدث خطأ!</p>
                </div>
                <p className="text-sm mt-1">يرجى المحاولة مرة أخرى.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم الكامل *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="أدخل اسمك الكامل"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-200"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف *
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="01xxxxxxxx"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-200"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@email.com"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-200"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  الرسالة
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="اكتب رسالتك هنا..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-200"
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="w-full flex items-center justify-center bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-3 px-6 rounded-full hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 shadow-md"
              >
                <Send className="w-5 h-5 ml-2" />
                إرسال الرسالة
              </button>
            </form>
            
            {/* قسم شهادات العملاء الجديد */}
            <div className="mt-2 pt-6 border-t border-gray-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-4 text-center">
                ماذا يقول عملاؤنا؟
              </h3>
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="bg-slate-50 p-4 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <Quote className="w-6 h-6 text-teal-500 -ml-2 rtl:mr-2 rtl:-ml-0" />
                      <StarRating rating={testimonial.rating} />
                    </div>
                    <p className="text-gray-700 text-right italic font-medium leading-relaxed mb-2 text-sm">
                      {testimonial.text}
                    </p>
                    <p className="text-xs font-bold text-blue-900 text-right">
                      - {testimonial.author}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* معلومات الاتصال (Contact Information) */}
          <div className="space-y-6">
            {contactInfo.map((info) => (
              <div key={info.id} id={info.id} className={`${info.color} p-6 rounded-2xl shadow-sm border border-gray-100`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white rounded-xl shadow-sm shrink-0">
                    {info.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {info.title}
                    </h3>
                    {info.details.map((detail, detailIndex) => (
                      <p key={detailIndex} className="text-gray-600 mb-1">
                        {detail}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            ))}

            {/* قسم الخريطة (Map Section) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <h3 className="text-xl font-bold text-blue-900 mb-4">
                مواقعنا على الخريطة
              </h3>
              <div className="h-[400px] w-full rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d219982.86937805233!2d31.057880586718742!3d30.514468800000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f7e735283825bf%3A0x821a669c64bffe42!2z2YXYudmF2YQg2KfZhNmG2K7YqNip!5e0!3m2!1sen!2seg!4v1784199395405!5m2!1sen!2seg"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="موقعنا على خرائط جوجل"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
